import { isBefore, startOfToday, isToday as isTodayLib } from 'date-fns'
import { groupBy, partition } from 'ramda'
import React, { useState } from 'react'
import { Button, Checkbox, Icon } from 'semantic-ui-react'
import useSWRMutation from 'swr/mutation'
import { Helmet } from 'react-helmet'

import useTasksAssignedToUser from '../../hooks/useTasksAssignedToUser'
import CollapsableSection from '../../components/CollapsableSection'
import FulfillTaskModal from '../../components/tasks/FulfillTaskModal'
import { useTalentApi } from '../../store/mainContext'
import TaskEditorModal from '../../components/tasks/TaskEditorModal'
import { Task, User } from '../../types'
import RequirePowers from '../../components/requirePowers'
import { useUser } from '../../pages/session/hooks'
import PageHeader from '../../components/pageHeader/pageHeader'
import UserSelect from '../../components/form/userSelect'
import PageLoader from '../../components/pageLoader'
import DataTable from '../../components/dataTable/dataTable'
import UserName from '../../components/remoteValues/userName'
import TaskRow from './taskRow'
import POWERS from '../../powers'
import BulkActionsBar from './BulkActionsBar'

type Dialog =
  | { type: 'complete-task'; task: Task }
  | { type: 'edit-task'; task: Task }
  | { type: 'create-task' }

const DONE_TASKS_PAGE_LENGTH = 10

export default function Tasks() {
  const user: any = useUser()
  const api = useTalentApi()

  const canBulkManageTasks = Boolean(user?.hasPower?.('master'))

  const [selectedUserId, setSelectedUserId] = useState<User['_id']>(user._id)
  const [currentDialog, setCurrentDialog] = React.useState<Dialog | null>(null)
  const [doneTasksCount, setDoneTasksCount] = React.useState(3)
  const [selectedTaskIds, setSelectedTaskIds] = React.useState<
    Record<Task['_id'], true>
  >({})
  const [isBulkActionInProgress, setIsBulkActionInProgress] =
    React.useState(false)

  const { trigger: removeTask } = useSWRMutation<Task[], Error, string[], Task>(
    ['tasks-assignedToId', selectedUserId],
    (_, { arg }) => api.delete(`tasks/${arg._id}`),
  )

  const { trigger: completeTask } = useSWRMutation<
    Task[],
    Error,
    string[],
    Task
  >(['tasks-assignedToId', selectedUserId], (_, { arg }) =>
    api.patch(`tasks/${arg._id}`, { isDone: true, doneNote: arg.doneNote }),
  )

  const { trigger: markAsNotDone } = useSWRMutation<
    Task[],
    Error,
    string[],
    Task
  >(['tasks-assignedToId', selectedUserId], (_, { arg }) =>
    api
      .patch(`tasks/${arg._id}`, { isDone: false })
      .then(() => api.delete(`tasks/${arg._id}/doneNote`)),
  )

  const tasks = useTasksAssignedToUser(selectedUserId)

  const selectedTasks = React.useMemo(
    () =>
      (tasks.data || []).filter(
        (task) => selectedTaskIds[task._id] && !task.isDone,
      ),
    [tasks.data, selectedTaskIds],
  )

  React.useEffect(() => {
    setSelectedTaskIds({})
  }, [selectedUserId])

  React.useEffect(() => {
    if (!canBulkManageTasks) {
      setSelectedTaskIds({})
    }
  }, [canBulkManageTasks])

  React.useEffect(() => {
    if (!tasks.data) return
    setSelectedTaskIds((prev) => {
      const selectableIds = new Set(
        (tasks.data || [])
          .filter((task) => !task.isDone)
          .map((task) => task._id),
      )
      let changed = false
      const next: Record<Task['_id'], true> = {}
      Object.keys(prev).forEach((id) => {
        if (selectableIds.has(id)) {
          next[id] = true
        } else {
          changed = true
        }
      })
      if (!changed && Object.keys(prev).length === Object.keys(next).length) {
        return prev
      }
      if (Object.keys(next).length === 0) {
        return {}
      }
      return next
    })
  }, [tasks.data])

  const handleSelectionChange = React.useCallback(
    (task: Task, isSelected: boolean) => {
      if (!canBulkManageTasks || task.isDone) return
      setSelectedTaskIds((prev) => {
        if (isSelected) {
          if (prev[task._id]) return prev
          return { ...prev, [task._id]: true }
        }
        if (!prev[task._id]) return prev
        const { [task._id]: _removed, ...rest } = prev
        return rest
      })
    },
    [canBulkManageTasks],
  )

  const toggleSectionSelection = React.useCallback(
    (sectionTasks: Task[], select: boolean) => {
      if (!canBulkManageTasks) return
      if (!sectionTasks.length) return
      setSelectedTaskIds((prev) => {
        const next = { ...prev }
        let changed = false
        if (select) {
          sectionTasks.forEach((task) => {
            if (task.isDone) return
            if (!next[task._id]) {
              next[task._id] = true
              changed = true
            }
          })
        } else {
          sectionTasks.forEach((task) => {
            if (next[task._id]) {
              delete next[task._id]
              changed = true
            }
          })
        }
        return changed ? next : prev
      })
    },
    [canBulkManageTasks],
  )

  const getSelectionState = React.useCallback(
    (sectionTasks: Task[]) => {
      const total = sectionTasks.length
      if (!total) {
        return { checked: false, indeterminate: false }
      }
      let selectedInSection = 0
      sectionTasks.forEach((task) => {
        if (selectedTaskIds[task._id]) {
          selectedInSection += 1
        }
      })
      return {
        checked: selectedInSection === total,
        indeterminate: selectedInSection > 0 && selectedInSection < total,
      }
    },
    [selectedTaskIds],
  )

  const selectedCount = selectedTasks.length
  const hasSelection = canBulkManageTasks && selectedCount > 0

  const [toggligTasks, setTogglingTasks] = useState<
    Record<Task['_id'], boolean>
  >({})

  const handleDone = async (task: Task) => {
    if (task.requireDoneNote && !task.doneNote) {
      setCurrentDialog({ type: 'complete-task', task })
      return
    }
    if (toggligTasks[task._id]) return
    setTogglingTasks((prev) => ({ ...prev, [task._id]: true }))
    return new Promise((resolve) => window.setTimeout(resolve, 1000)).then(
      async () => {
        return completeTask(task, {
          revalidate: false,
          optimisticData: (current) =>
            (current || []).map((t) =>
              task._id === t._id ? { ...t, isDone: true } : t,
            ),
        })
          .then(() => tasks.mutate())
          .then(() => {
            setTogglingTasks((prev) => ({ ...prev, [task._id]: false }))
          })
          .then(() => setCurrentDialog(null))
      },
    )
  }

  const handleNotDone = async (task: Task) => {
    return markAsNotDone(task, {
      optimisticData: (current) =>
        (current || []).map((t) => {
          const { doneNote, ...other } = t
          return task._id === t._id ? { ...other, isDone: false } : t
        }),
    })
  }

  const handleRemove = (task: Task) => {
    return removeTask(task, {
      optimisticData: (current) =>
        (current || []).filter((t) => task._id !== t._id),
    }).then(() => {
      setSelectedTaskIds((prev) => {
        if (!prev[task._id]) return prev
        const { [task._id]: _removed, ...rest } = prev
        return rest
      })
    })
  }

  if (tasks.isLoading) return <PageLoader />

  const [doneTasks, notDoneTasks] = partition<Task>((task) => !!task.isDone)(
    tasks.data || [],
  )

  // Group pending tasks in `past`, `today` & `future` based on their `dueDate`
  const { today, past, future } = groupBy<Task>((task) => {
    return isTodayLib(task.dueDate)
      ? 'today'
      : isBefore(task.dueDate, startOfToday())
        ? 'past'
        : 'future'
  })(notDoneTasks || [])

  const todayAndPastTasks = [...(past || []), ...(today || [])]
  const futureTasksList = future || []

  const todayAndPastSelection = getSelectionState(todayAndPastTasks)
  const futureSelection = getSelectionState(futureTasksList)

  const overdueAndDueTodayCount = (today?.length || 0) + (past?.length || 0)

  return (
    <React.Fragment>
      <Helmet>
        <title>Tasks</title>
      </Helmet>

      <PageHeader
        title="Tasks"
        breadcrumb={[
          { text: 'Dashboard', link: '/' },
          { text: 'Tasks', link: '/tasks' },
        ]}
        sub="One list to do it all."
        actions={
          <div className="flex">
            <RequirePowers powers={[POWERS.master]}>
              <UserSelect
                onChange={(userId) => setSelectedUserId(userId)}
                clerable={false}
                value={selectedUserId}
              />
            </RequirePowers>

            <Button
              onClick={() => setCurrentDialog({ type: 'create-task' })}
              style={{ marginLeft: 10 }}
              basic
            >
              <Icon name="add" /> Add Task
            </Button>
          </div>
        }
      />

      <div className="mx-14 my-8">
        {canBulkManageTasks && hasSelection && (
          <BulkActionsBar
            selectedTasks={selectedTasks}
            isProcessing={isBulkActionInProgress}
            onProcessingChange={setIsBulkActionInProgress}
            setSelectedTaskIds={setSelectedTaskIds}
            mutateTasks={tasks.mutate}
          />
        )}

        <CollapsableSection
          title={`Today & Overdue (${overdueAndDueTodayCount})`}
        >
          <React.Fragment>
            {overdueAndDueTodayCount > 0 && (
              <>
                {(past || []).length > 0 ? (
                  <p className="ml-11 pb-4 pt-1 text-base text-slate-500">
                    Hey... came on! catch up with your{' '}
                    <b style={{ color: 'red' }}>due tasks!</b>
                  </p>
                ) : (
                  <p className="ml-11 pb-4 pt-1 text-base text-slate-500">
                    <b style={{ color: 'var(--primary)' }}>Awesome!</b> you are
                    up to date! don&apos;t let your tasks to expire!
                  </p>
                )}
                <DataTable>
                  <thead>
                    <tr>
                      {canBulkManageTasks && (
                        <DataTable.Th width="1%" align="center">
                          <Checkbox
                            aria-label="Select section tasks"
                            checked={todayAndPastSelection.checked}
                            indeterminate={todayAndPastSelection.indeterminate}
                            disabled={
                              isBulkActionInProgress ||
                              todayAndPastTasks.length === 0
                            }
                            onChange={(_, data) =>
                              toggleSectionSelection(
                                todayAndPastTasks,
                                Boolean(data.checked),
                              )
                            }
                          />
                        </DataTable.Th>
                      )}
                      <DataTable.Th width="1%" align="center">
                        Done
                      </DataTable.Th>
                      <DataTable.Th width={200}>Subject</DataTable.Th>
                      <DataTable.Th>Details</DataTable.Th>
                      <DataTable.Th width="1%" className="whitespace-nowrap">
                        Related To
                      </DataTable.Th>
                      <DataTable.Th width="1%" className="whitespace-nowrap">
                        Due Date
                      </DataTable.Th>
                      <DataTable.Th
                        width="1%"
                        className="text-nowrap"
                        align="left"
                      >
                        Assigned By
                      </DataTable.Th>
                      <DataTable.Th width="1%"></DataTable.Th>
                    </tr>
                  </thead>

                  <tbody>
                    {[...(past || []), ...(today || [])].map((task) => {
                      const isToggling = Boolean(toggligTasks[task._id])
                      return (
                        <TaskRow
                          key={task._id}
                          task={task}
                          onEdit={() =>
                            setCurrentDialog({ type: 'edit-task', task })
                          }
                          onRemove={handleRemove}
                          onToggle={handleDone}
                          isToggling={isToggling}
                          isSelected={Boolean(selectedTaskIds[task._id])}
                          onSelectionChange={handleSelectionChange}
                          selectionDisabled={isBulkActionInProgress}
                          showSelection={canBulkManageTasks && !task.isDone}
                        />
                      )
                    })}
                  </tbody>
                </DataTable>
              </>
            )}

            {overdueAndDueTodayCount === 0 && (
              <p className="ml-11 pb-4 pt-1 text-base text-slate-500">
                No tasks yet! :)
              </p>
            )}
          </React.Fragment>
        </CollapsableSection>

        <CollapsableSection className="mb-1 mt-16" title="Coming Next">
          <React.Fragment>
            {(future || []).length > 0 && (
              <>
                <div className="ml-11 pb-8 pt-1 text-base text-slate-500">
                  <UserName id={selectedUserId} short />
                  &apos;s future problems, don&apos;t worry about them now.
                </div>
                <DataTable>
                  <thead>
                    <tr>
                      {canBulkManageTasks && (
                        <DataTable.Th width="1%" align="center">
                          <Checkbox
                            aria-label="Select upcoming tasks"
                            checked={futureSelection.checked}
                            indeterminate={futureSelection.indeterminate}
                            disabled={
                              isBulkActionInProgress ||
                              futureTasksList.length === 0
                            }
                            onChange={(_, data) =>
                              toggleSectionSelection(
                                futureTasksList,
                                Boolean(data.checked),
                              )
                            }
                          />
                        </DataTable.Th>
                      )}
                      <DataTable.Th width={85} align="center">
                        Done
                      </DataTable.Th>
                      <DataTable.Th width={200}>Subject</DataTable.Th>
                      <DataTable.Th>Details</DataTable.Th>
                      <DataTable.Th width={180}>Related To</DataTable.Th>
                      <DataTable.Th width={180} align="left">
                        Due Date
                      </DataTable.Th>
                      <DataTable.Th width={200} align="left">
                        Assigned By
                      </DataTable.Th>
                      <DataTable.Th width={250}></DataTable.Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(future || []).map((task) => {
                      const isToggling = Boolean(toggligTasks[task._id])
                      return (
                        <TaskRow
                          key={task._id}
                          task={task}
                          onEdit={() =>
                            setCurrentDialog({ type: 'edit-task', task })
                          }
                          onRemove={handleRemove}
                          onToggle={handleDone}
                          isToggling={isToggling}
                          isSelected={Boolean(selectedTaskIds[task._id])}
                          onSelectionChange={handleSelectionChange}
                          selectionDisabled={isBulkActionInProgress}
                          showSelection={canBulkManageTasks && !task.isDone}
                        />
                      )
                    })}
                  </tbody>
                </DataTable>
              </>
            )}

            {(future || []).length === 0 && (
              <p className="pb-4 text-base text-slate-500">No tasks yet! :)</p>
            )}
          </React.Fragment>
        </CollapsableSection>

        {doneTasks.length > 0 && (
          <CollapsableSection className="mb-1 mt-16" title="Done">
            <React.Fragment>
              <p className="ml-11 pb-4 pt-1 text-base text-slate-500">
                Task you completed. :)
              </p>

              <DataTable>
                <thead>
                  <tr>
                    <DataTable.Th width="1%" align="center">
                      Done
                    </DataTable.Th>
                    <DataTable.Th width={200}>Subject</DataTable.Th>
                    <DataTable.Th>Details</DataTable.Th>
                    <DataTable.Th width="1%" className="whitespace-nowrap">
                      Related To
                    </DataTable.Th>
                    <DataTable.Th width="1%" className="whitespace-nowrap">
                      Done Date
                    </DataTable.Th>
                    <DataTable.Th
                      width="1%"
                      className="text-nowrap"
                      align="left"
                    >
                      Assigned By
                    </DataTable.Th>
                    <DataTable.Th width="1%"></DataTable.Th>
                  </tr>
                </thead>
                <tbody>
                  {doneTasks
                    .sort((a, b) => (b.doneOn || 0) - (a.doneOn || 0))
                    .slice(0, doneTasksCount)
                    .map((task) => (
                      <TaskRow
                        noLineThrough
                        key={task._id}
                        task={task}
                        onEdit={() =>
                          setCurrentDialog({ type: 'edit-task', task })
                        }
                        onToggle={handleNotDone}
                        onRemove={handleRemove}
                        isSelected={Boolean(selectedTaskIds[task._id])}
                        onSelectionChange={handleSelectionChange}
                        selectionDisabled={isBulkActionInProgress}
                        showSelection={false}
                      />
                    ))}
                </tbody>
              </DataTable>
              {doneTasksCount <= doneTasks.length && (
                <div className="mt-6">
                  <Button
                    onClick={() =>
                      setDoneTasksCount((prev) => prev + DONE_TASKS_PAGE_LENGTH)
                    }
                    content="View more"
                    compact
                    basic
                  />
                </div>
              )}
            </React.Fragment>
          </CollapsableSection>
        )}

        {(currentDialog?.type === 'create-task' ||
          currentDialog?.type === 'edit-task') && (
          <TaskEditorModal
            assignedToId={selectedUserId}
            onClose={() => setCurrentDialog(null)}
            onSuccess={() => tasks.mutate().then(() => setCurrentDialog(null))}
            task={
              currentDialog?.type === 'edit-task'
                ? currentDialog.task
                : undefined
            }
          />
        )}

        {currentDialog?.type === 'complete-task' && (
          <FulfillTaskModal
            onMarkAsDone={handleDone}
            onClose={() => setCurrentDialog(null)}
            task={currentDialog.task}
          />
        )}
      </div>
    </React.Fragment>
  )
}
